import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { api, ApiError } from '@/api'
import type { User } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isPublisher = computed(() => ['publisher', 'admin'].includes(user.value?.role || ''))
  const username = computed(() => user.value?.username || '')

  async function login(username: string, password: string): Promise<void> {
    loading.value = true
    try {
      await api.auth.login(username, password)
      await fetchUser()
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    loading.value = true
    try {
      await api.auth.logout()
      user.value = null
    } finally {
      loading.value = false
    }
  }

  async function fetchUser(): Promise<User | null> {
    try {
      user.value = await api.auth.me()
      return user.value
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        user.value = null
      }
      return null
    } finally {
      initialized.value = true
    }
  }

  return {
    user,
    initialized,
    isAuthenticated,
    isAdmin,
    isPublisher,
    username,
    login,
    logout,
    fetchUser
  }
})
